import { Card, Space, Steps } from 'antd';
import { useContext, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import PPPageHeader from '../../components/pageHeader/PPPageHeader';
import useStore from '../../store/useStore';
import ProductAdd from './Add';
import { productDraftKey, useClearDraftOnLeave } from './draft';
import ProductCheck from './ProductCheck';
import { LabelContext, ProductProvider } from './ProductDataContext';
import ProductVersion from './ProductVersion';

const routes = [
  {
    path: '/merchant/product/list',
    breadcrumbName: 'Бараа',
  },
  {
    path: 'first',
    breadcrumbName: 'Нэгж бараа',
  },
];

const STEP_INFO = {
  title: 'Бараа',
  description: 'Барааны мэдээлэл',
  code: 'PRODUCT_INFO',
};
const STEP_VERSION = {
  title: 'Хувилбар',
  description: 'Хувилбарууд үүсгэх',
  code: 'PRODUCT_VERSION',
};
const STEP_CHECK = {
  title: 'Хянах',
  description: 'Мэдээлэл хянах',
  code: 'PRODUCT_CHECK',
};

/**
 * Алхмын удирдлага. Бөглөсөн мэдээлэл нь draft-аар sessionStorage-д хадгалагддаг
 * тул (ProductDataContext) refresh хийхэд ч сэргэнэ.
 */
const ProductStepContent = () => {
  const history = useHistory();
  const location = useLocation();
  const { labelInfo, maxStep, setMaxStep } = useContext(LabelContext);

  /**
   * "Хувилбартай бараа юу?" сонголт нь контекстэд байдаг тул refresh хийхэд
   * алхмуудын тоо хэвээрээ сэргэнэ.
   */
  const hasVariant = labelInfo?.productItem?.hasVariant;
  const steps = hasVariant ? [STEP_INFO, STEP_VERSION, STEP_CHECK] : [STEP_INFO, STEP_CHECK];

  /**
   * Алхмыг URL-ийн `?step=1..N` агуулна — refresh болон browser-ийн
   * back/forward хоёулаа ажиллана.
   */
  const stepParam = Number(new URLSearchParams(location.search).get('step'));
  const urlStep =
    Number.isInteger(stepParam) && stepParam >= 1 ? Math.min(stepParam - 1, steps.length - 1) : 0;

  /**
   * Эхний рендер дээр л: батлаагүй алхам руу шууд орохыг хориглоно (жиш нь
   * шинэ session дээр `?step=3` гэж гараар оровол 1-р алхмаас эхэлнэ).
   */
  const isMounted = useRef(false);
  const current = isMounted.current ? urlStep : Math.min(urlStep, maxStep);

  /**
   * Алхам солихдоо шинэ history бичлэг үүсгэнэ — browser-ийн back дарахад
   * өмнөх алхам руу буцна. Зөвхөн зөвшөөрөгдөөгүй алхмыг залруулахдаа
   * `replace` хийнэ (буцах товч дахин тэр алхам руу оруулахгүй).
   */
  const goToStep = (step, { replace = false } = {}) => {
    const target = { pathname: location.pathname, search: `?step=${step + 1}` };
    return replace ? history.replace(target) : history.push(target);
  };

  useEffect(() => {
    isMounted.current = true;
    if (urlStep > maxStep) goToStep(maxStep, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const next = () => {
    setMaxStep((step) => Math.max(step, current + 1));
    goToStep(current + 1);
  };
  const prev = () => goToStep(current - 1);

  const stepCode = steps[current]?.code;

  return (
    <>
      <PPPageHeader
        title="Нэгж бараа үүсгэх"
        breadcrumb={{ routes }}
        onBack={() => history.push('/merchant/product/list')}
      />

      <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
        <Card size="middle">
          {/**
           * Гарчиг дээр дарж ӨМНӨХ алхам руу буцаж болно. Урагшаа алхам нь
           * disabled — мэдээллийг батлахгүйгээр цааш харайхыг хориглоно.
           */}
          <Steps
            current={current}
            responsive
            size="small"
            onChange={(step) => step < current && goToStep(step)}
            items={steps.map(({ title, description }, index) => ({
              title,
              description,
              disabled: index > current,
            }))}
          />
        </Card>

        {stepCode === 'PRODUCT_INFO' && <ProductAdd next={next} steps={steps} current={current} />}

        {stepCode === 'PRODUCT_VERSION' && (
          <ProductVersion next={next} prev={prev} steps={steps} current={current} />
        )}

        {stepCode === 'PRODUCT_CHECK' && (
          <ProductCheck next={next} prev={prev} steps={steps} current={current} />
        )}
      </Space>
    </>
  );
};

const ProductStep = () => {
  /**
   * Draft-ыг merchant-аар салгана — merchant солиход (нэг таб дотор reload
   * болдог тул) өмнөх merchant дээр бөглөсөн мэдээлэл сэргэхгүй.
   */
  const merchantCode = useStore((state) => state.user.merchantCode);
  const draftKey = productDraftKey(merchantCode);
  // Хуудсаас гармагц бөглөсөн мэдээлэл цэвэрлэгдэнэ (refresh дээр сэргэсэн хэвээр)
  useClearDraftOnLeave(draftKey);

  return (
    // `key` нь draftKey — merchant солиход provider remount болж, өмнөх
    // merchant-ийн төлөв шинэ draft key дор бичигдэхээс сэргийлнэ
    <ProductProvider key={draftKey} draftKey={draftKey}>
      <ProductStepContent />
    </ProductProvider>
  );
};

export default ProductStep;
